import { CartFactory, CartMapperParameter } from "@/v2/models/carts.js";
import httpMixin from "@/v2/mixins/http.js";
import mapperMixin from "@/v2/mixins/mapper.js";
import productMixin from "@/v2/mixins/products.js";
import documentMixin from "@/v2/mixins/documents.js";
import mediaMixin from "@/v2/mixins/medias.js";

export default {
    mixins: [httpMixin, mapperMixin, productMixin, documentMixin, mediaMixin],
    computed: {
        taxIncluded: {
            get() {
                return window.bo.taxIncluded;
            },
        },
        displayPricesDuringCartAndCheckoutIncludingTax: {
            get() {
                return window.bo.displayPricesDuringCartAndCheckoutIncludingTax;
            },
        },
        taxExempted: {
            get() {
                return window.bo.taxExempted;
            },
        },
    },
    methods: {
        _cartMapper(cart) {
            return {
                id: cart.id,
                items: cart.items,
                attachments: cart.attachments,
                return_attachments: cart.return_attachments,
                remark: cart.remark,
                isTaxIncluded: window.bo.taxIncluded,
                showTax: window.bo.displayPricesDuringCartAndCheckoutIncludingTax,
                isTaxExempted: window.bo.taxExempted,
                mode: cart.mode,
                shipping_address: cart.shipping_address,
                shipping_method: cart.shipping_method,
                payment_mode: cart.payment_mode,
                preferred_delivery_date: cart.preferred_delivery_date,
                custom_fields: cart.custom_fields,
                siteCurrency: window.bo.siteCurrency,
                siteCurrencyDecimalPlaces: window.bo.siteCurrencyDecimalPlaces,
                shippingFeeTaxClass: window.bo.shippingFeeTaxClass,
            };
        },

        _cartItemMapper(cartItem) {
            return {
                id: cartItem.id,
                line_item_id: cartItem.line_item_id,
                document_number: cartItem.document_number,
                line_item: cartItem.line_item,
                product_id: cartItem.product_id,
                product: cartItem.product,
                quantity: cartItem.quantity,
                ratio: cartItem.quantity,
                regular_price: cartItem.regular_price,
                regular_tax: cartItem.regular_tax,
                unit_price: cartItem.unit_price,
                unit_tax: cartItem.unit_tax,
                total: cartItem.total,
                subtotal: cartItem.subtotal,
                total_tax: cartItem.total_tax,
                subtotal_tax: cartItem.subtotal_tax,
                composite_parent_id: cartItem.composite_parent_id,
                tax_class: cartItem.tax_class,
                company_id: cartItem.company_id,
                component_id: cartItem.component_id,
                remark: cartItem.remark,
                return_reason: cartItem.return_reason,
                batch: cartItem.batch,
                serial_numbers: cartItem.serial_numbers,
                attachments: cartItem.attachments,
                isTaxIncluded: window.bo.taxIncluded,
                showTax: window.bo.displayPricesDuringCartAndCheckoutIncludingTax,
                isTaxExempted: window.bo.taxExempted,
                shipping_address: cartItem.shipping_address,
                mode: cartItem.mode,
                product_details_url: cartItem.product_details_url,
                isFreeQuantity: cartItem.isFreeQuantity,
                has_volume_pricing: cartItem.has_volume_pricing ?? false,
                siteCurrency: window.bo.siteCurrency,
                siteCurrencyDecimalPlaces: window.bo.siteCurrencyDecimalPlaces,
                roundLineItemDiscount: window.bo.roundLineItemDiscount,
            };
        },

        parseCart(cart) {
            return CartFactory.construct(this.createMapperParameter(new CartMapperParameter()), cart);
        },

        getCart(id, url, payload, success, failure) {
            return this.getData(
                url + "/" + id,
                payload,
                (response) => {
                    success(this.parseCart(response.data));
                },
                failure
            );
        },

        postCart(url, payload, success, failure) {
            return this.postData(
                url,
                payload,
                (response) => {
                    success(this.parseCart(response.data));
                },
                failure
            );
        },

        postAddToCart(url, payload, success, failure) {
            return this.postData(
                url + "/add_to_cart",
                payload,
                (response) => {
                    success(this.parseCart(response.data));
                },
                failure
            );
        },

        updateCartItems(url, payload, success, failure) {
            return this.postData(url + "/items/update", payload, success, failure);
        },

        checkoutCart(url, payload, success, failure) {
            return this.postData(
                url + "/checkout",
                payload,
                (response) => {
                    if (success) success(response.data);
                },
                failure
            );
        },

        deleteCartItems(url, payload, success, failure) {
            return this.postData(url + "/items/delete", payload, success, failure);
        },

        calculateShippingFee(url, id, shippingMethodIds, success, failure, showLoadingScreen = true) {
            if (shippingMethodIds instanceof Array) {
                shippingMethodIds = shippingMethodIds.join(",");
            }

            return this.getData(
                `${url}/${id}/calculate-shipping-fee?shipping_method_id=${shippingMethodIds}`,
                null,
                (response) => {
                    success(response.data);
                },
                failure,
                showLoadingScreen
            );
        },

        updateAttachments(url, id, attachments, success, failure) {
            return this.putData(
                `${url}/${id}/attachments`,
                {
                    attachments: attachments,
                },
                (response) => {
                    success(this.parseMedias(response.data));
                },
                failure
            );
        },

        updateReturnAttachments(url, id, attachments, success, failure) {
            return this.putData(
                `${url}/${id}/return-attachments`,
                {
                    attachments: attachments,
                },
                (response) => {
                    success(this.parseMedias(response.data));
                },
                failure
            );
        },

        getTradeAgreementPrices(url, productId, payload, success, failure, showLoadingScreen = true) {
            return this.getData(
                `${url}/trade-agreement/${productId}/prices`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure,
                showLoadingScreen
            );
        },

        getProductTradeAgreementPrice(url, productId, payload, success, failure, showLoadingScreen = true) {
            return this.postData(
                `${url}/trade-agreement/${productId}`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure,
                showLoadingScreen
            );
        },
        getAllProductTradeAgreementPrice(url, payload, success, failure) {
            return this.postData(
                `${url}/trade-agreement/allProduct`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },
        getCartTradeAgreementPrice(url, payload, success, failure, showLoadingScreen = true) {
            return this.postData(
                url,
                payload,
                (response) => {
                    success(response.data);
                },
                failure,
                showLoadingScreen
            );
        },
    },
};
